import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, Button, Empty, Input, Spin, Tag, message } from 'antd';
import { MessageOutlined, ReloadOutlined, SendOutlined, UserOutlined } from '@ant-design/icons';
import { smsApi } from '../../utils/api';
import { resolveAvatarSrc } from '../../utils/avatarUtils';
import { toPersianDigits } from '../../utils/formatPriceToman';

const { TextArea } = Input;

const STATUS_LABEL = {
  sent: { text: 'ارسال شده', color: 'processing' },
  delivered: { text: 'تحویل شده', color: 'success' },
  failed: { text: 'ناموفق', color: 'error' },
  unread: { text: 'خوانده‌نشده', color: 'warning' },
  read: { text: 'خوانده‌شده', color: 'default' },
};

/**
 * تاریخچه پیامک — چیدمان بلوکی ساده بدون flex/absolute تا روی‌هم‌افتادگی رخ ندهد
 */
const SmsHistoryPanel = ({ entityType, entityId, recipientName }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const listRef = useRef(null);
  const markedRef = useRef(new Set());

  const sendType = entityType === 'deal' ? 'deal' : entityType === 'client' ? 'client' : 'lead';

  const fetchHistory = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      let res;
      if (entityType === 'deal') res = await smsApi.getDealHistory(entityId);
      else if (entityType === 'client') res = await smsApi.getClientHistory(entityId);
      else res = await smsApi.getLeadHistory(entityId);

      const list = Array.isArray(res?.messages)
        ? res.messages
        : Array.isArray(res?.data?.messages)
          ? res.data.messages
          : Array.isArray(res?.data)
            ? res.data
            : [];
      setMessages(list);

      const unreadIncoming = list.filter(
        (m) => m.direction === 'incoming' && (m.status === 'unread' || !m.status) && m.id != null,
      );
      await Promise.all(
        unreadIncoming.map(async (m) => {
          if (markedRef.current.has(m.id)) return;
          try {
            await smsApi.markRead(m.id);
            markedRef.current.add(m.id);
          } catch {
            // ignore
          }
        }),
      );
    } catch (error) {
      console.error('SMS history load failed:', error);
      message.error(error?.message || 'خطا در دریافت تاریخچه پیامک');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    markedRef.current = new Set();
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    if (loading) return;
    const el = listRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages, loading]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) {
      message.warning('لطفاً متن پیامک را وارد کنید');
      return;
    }
    setSending(true);
    try {
      await smsApi.send({ type: sendType, id: Number(entityId), message: text });
      message.success('پیامک ارسال شد');
      setDraft('');
      await fetchHistory();
    } catch (error) {
      message.error(error?.message || 'خطا در ارسال پیامک');
    } finally {
      setSending(false);
    }
  };

  const formatPhone = (value) => {
    if (!value) return '';
    return toPersianDigits(String(value));
  };

  return (
    <div className="custom-font sms-history-root">
      <style>{`
        .sms-history-root {
          display: block !important;
          width: 100% !important;
          position: static !important;
          clear: both !important;
        }
        .sms-history-root * {
          box-sizing: border-box;
        }
        .sms-history-list {
          display: block !important;
          width: 100% !important;
          height: 340px !important;
          max-height: 340px !important;
          min-height: 200px !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          position: static !important;
          margin: 0 0 16px 0 !important;
          padding: 12px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 12px !important;
          background: #f8fafc !important;
          -webkit-overflow-scrolling: touch;
        }
        .dark .sms-history-list {
          border-color: #334155 !important;
          background: rgba(15, 23, 42, 0.55) !important;
        }
        .sms-history-composer {
          display: block !important;
          width: 100% !important;
          position: static !important;
          clear: both !important;
          margin: 0 !important;
          padding: 12px !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 12px !important;
          background: #ffffff !important;
        }
        .dark .sms-history-composer {
          border-color: #334155 !important;
          background: #1e293b !important;
        }
        .sms-history-composer .ant-input-textarea,
        .sms-history-composer .ant-input-textarea-affix-wrapper,
        .sms-history-composer textarea {
          position: static !important;
          margin: 0 !important;
        }
        .sms-msg-row {
          display: block !important;
          width: 100% !important;
          margin: 0 0 12px 0 !important;
          overflow: visible !important;
        }
        .sms-msg-row:last-child {
          margin-bottom: 0 !important;
        }
        .sms-msg-bubble {
          display: inline-block !important;
          max-width: 420px !important;
          width: auto !important;
          vertical-align: top;
          padding: 10px 14px !important;
          border-radius: 16px !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04);
        }
        .sms-msg-out {
          float: right !important;
          clear: both !important;
          background: #ecfdf5 !important;
          border: 1px solid #a7f3d0 !important;
          border-bottom-right-radius: 6px !important;
        }
        .sms-msg-in {
          float: left !important;
          clear: both !important;
          background: #ffffff !important;
          border: 1px solid #e2e8f0 !important;
          border-bottom-left-radius: 6px !important;
        }
        .dark .sms-msg-out {
          background: rgba(6, 78, 59, 0.35) !important;
          border-color: rgba(16, 185, 129, 0.35) !important;
        }
        .dark .sms-msg-in {
          background: #1e293b !important;
          border-color: #475569 !important;
        }
        .sms-history-clearfix {
          display: block !important;
          clear: both !important;
          height: 0 !important;
          overflow: hidden !important;
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <div className="text-sm text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
          <MessageOutlined className="text-sky-500" />
          تاریخچه پیامک‌ها
          {recipientName ? (
            <span className="text-slate-400 text-xs">· {recipientName}</span>
          ) : null}
        </div>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          className="custom-font"
          onClick={fetchHistory}
          loading={loading}
        >
          بروزرسانی
        </Button>
      </div>

      <div ref={listRef} className="sms-history-list">
        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <Spin />
          </div>
        ) : messages.length === 0 ? (
          <Empty
            description={<span className="custom-font">هنوز پیامکی رد و بدل نشده است</span>}
            className="custom-font"
            style={{ padding: '40px 0' }}
          />
        ) : (
          <>
            {messages.map((msg) => {
              const outgoing = msg.direction === 'outgoing';
              const statusMeta = STATUS_LABEL[msg.status] || null;
              const timeLabel =
                msg.created_at_jalali ||
                msg.received_at ||
                msg.sent_at ||
                msg.created_at ||
                '';
              const senderName = msg.sender?.name;
              const avatarSrc = resolveAvatarSrc(msg.sender?.image);

              return (
                <div key={msg.id} className="sms-msg-row">
                  <div className={`sms-msg-bubble ${outgoing ? 'sms-msg-out' : 'sms-msg-in'}`}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      {outgoing ? (
                        <Avatar size={28} src={avatarSrc} icon={<UserOutlined />} />
                      ) : null}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            flexWrap: 'wrap',
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
                            {outgoing
                              ? `ارسال‌شده توسط: ${senderName || '—'}`
                              : `از: ${formatPhone(msg.from_number) || 'مخاطب'}`}
                          </span>
                          {statusMeta ? (
                            <Tag color={statusMeta.color} className="custom-font m-0 text-[10px]">
                              {statusMeta.text}
                            </Tag>
                          ) : null}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            color: '#0f172a',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            lineHeight: 1.6,
                          }}
                        >
                          {msg.message}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
                          {timeLabel}
                          {msg.to_number ? (
                            <span style={{ marginRight: 8 }}>
                              · به {formatPhone(msg.to_number)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="sms-history-clearfix" />
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="sms-history-composer">
        <TextArea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 500))}
          placeholder="پیامک جدید بنویسید..."
          maxLength={500}
          className="custom-font"
          onPressEnter={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 10,
            gap: 8,
          }}
        >
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            {toPersianDigits(draft.length)} / {toPersianDigits(500)}
          </span>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSend}
            className="custom-font bg-green-600 hover:bg-green-700 border-0"
          >
            ارسال پیامک
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SmsHistoryPanel;
